// Die Dachplanung mit K2 (Mockup /doku/k2; E2, Schritt 3). Das Dach plant
// der Verkäufer mit K2 am Surface – unser System plant kein Dach. Hier legt
// er das PDF samt Stückliste ab, oder er überspringt den Schritt. Kein
// Pflichtschritt: Das PDF kann jederzeit nachkommen, auch nach dem Senden
// (Plan E2, „Ich entscheide“). Was in Stufe 2 dazukommt – K2 direkt öffnen,
// die Stückliste auslesen, die Unterschrift des Kunden –, steht als
// Platzhalter an seinem Platz (CLAUDE.md, Grundsatz 8). Wo die Dachplanung
// im Ablauf hingehört, ist eine offene Frage an Manuel (O15) – sie steht
// dezent dabei, wie im Mockup.
//
// Seit Schritt 4: Nach „Weiter“ oder „Überspringen“ geht es zu
// „Abschließen“, wenn alle Stationen fertig sind. Die Seite bleibt auch nach
// dem Senden bedienbar (das PDF darf nachkommen). „PDF ansehen“ öffnet das
// abgelegte PDF in einem neuen Tab – vom Gerät oder, nach dem Senden, aus
// dem Büro (schon beim Öffnen der Seite geholt, nur im Arbeitsspeicher).
// „PDF entfernen“ steht seit 20.10.2026 bei den anderen PDF-Knöpfen.
//
// Seit E3c (S5; ANFORDERUNGEN.md H24, G19): oben ein Satz, wie es geht, und
// „Dachplanung starten“ – öffnet K2 unter der Adresse, die die Leitung
// eingetragen hat (Einstellungen „Lieferanten und Partien“), in einem neuen
// Tab; ohne Adresse fehlt der Knopf. Die Adresse liegt am Gerät (material.ts,
// mit der Standardliste geholt) – der Knopf ist auch ohne Netz da. Darunter
// zwei Plätze: der Plan (PDF, wie bisher) für die Dachpartie und die Liste
// (der Excel-Report von K2, .xlsx) für den Lieferanten – beide dürfen
// später kommen, auch nach dem Senden. Die offene Frage an Manuel, wohin die
// Dachplanung gehört (O15), ist mit H24 beantwortet: in die Besichtigung,
// draußen beim Kunden – der Hinweis dazu ist weg.
//
// Adresse: #/aufnahme/<kennung>/k2

import { useCallback, useEffect, useRef, useState } from "react";
import { XLSX, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, fehlerMelden, Ikon, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { aenderbar, k2ExcelAblegen, k2PdfAblegen, k2Ueberspringen, type PdfBereit } from "../arbeitskopie.ts";
import type { K2 } from "../speicher.ts";
import { STATIONEN_ZAHL, stationenFertig } from "../postausgang.ts";
import { dateiZumAnsehen, holenFehlgeschlagen, type DateiStand } from "../dateien.ts";
import { excelVorbereiten, groesseZeigen, k2AdresseZumOeffnen, pdfVorbereiten, type PdfPruefung } from "../k2.ts";
import { k2AdresseLesen, standardlisteAnstossen } from "../material.ts";
import { aufnahmeLesen, k2Erledigt, type AufnahmeGanz } from "./Uebersicht.tsx";
import { keinPlatz, SendeStand, useLive, usePostausgang, ZUM_START } from "./gemeinsam.tsx";

/** Was das Feld annimmt: das PDF der Dachplanung – oder (seit S5) den Excel-Report. */
const NUR = { pdf: "application/pdf,.pdf", excel: `.xlsx,${XLSX}` } as const;

/** Das unsichtbare Feld „Datei wählen“ – nur PDF (oder .xlsx); geleert, damit dieselbe Datei noch einmal gewählt werden kann. */
function PdfFeld({ gewaehlt, art = "pdf" }: { gewaehlt: (datei: File) => void; art?: keyof typeof NUR }) {
  return (
    <input
      type="file"
      className="datei-waehler"
      accept={NUR[art]}
      onChange={(e) => {
        const datei = e.target.files?.[0];
        e.target.value = "";
        if (datei) gewaehlt(datei);
      }}
    />
  );
}

/**
 * „PDF ansehen“: öffnet das PDF in einem neuen Tab über eine blob-Adresse –
 * immer mit EINEM Tipp. Liegt es am Gerät, öffnet es der Tipp sofort. Liegt
 * es nur im Büro (nach dem Senden), holt die Seite es schon beim Öffnen –
 * nur in den Arbeitsspeicher –, und „PDF ansehen“ ist dann ein Link. Warum
 * vorher: Das iPhone öffnet einen neuen Tab nur direkt aus dem Tipp heraus;
 * holte erst der Tipp das PDF, brauchte es einen zweiten (Gernots Augentest,
 * 20.10.2026). Ohne Empfang holt die Seite es, sobald das Netz zurück ist.
 * Die Adressen gibt die Seite beim Verlassen frei.
 */
function PdfAnsehen({ k2 }: { k2: K2 }) {
  const ausDemBuero = !k2.pdf && k2.pruefsumme !== null;
  const [stand, setStand] = useState<DateiStand | null>(ausDemBuero ? { art: "holt" } : null);
  // Zählt die Versuche hoch – jeder neue holt das PDF noch einmal (Netz zurück, „Noch einmal“).
  const [versuch, setVersuch] = useState(0);
  const adressen = useRef<string[]>([]);
  useEffect(() => {
    const liste = adressen.current;
    return () => {
      for (const url of liste) URL.revokeObjectURL(url);
    };
  }, []);

  function adresse(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    adressen.current.push(url);
    return url;
  }

  const nochEinmal = useCallback(() => {
    setStand({ art: "holt" });
    setVersuch((n) => n + 1);
  }, []);

  // Nur im Büro: gleich beim Öffnen der Seite holen.
  useEffect(() => {
    if (!ausDemBuero || !k2.pruefsumme) return;
    let gilt = true;
    dateiZumAnsehen(k2.pruefsumme, "application/pdf").then(
      (blob) => {
        if (!gilt) return;
        if (!blob) {
          setStand({ art: "fehlt" });
          return;
        }
        const url = URL.createObjectURL(blob);
        adressen.current.push(url);
        setStand({ art: "da", url });
      },
      (fehler: unknown) => {
        if (!gilt) return;
        const s = holenFehlgeschlagen(fehler);
        if (s.art === "fehlt") fehlerMelden("doku", fehler);
        setStand(s);
      },
    );
    return () => {
      gilt = false;
    };
  }, [ausDemBuero, k2.pruefsumme, versuch]);

  // Ohne Empfang: sobald das Netz zurück ist, noch einmal.
  const ohneNetz = stand?.art === "ohne-netz";
  useEffect(() => {
    if (!ohneNetz) return;
    window.addEventListener("online", nochEinmal);
    return () => window.removeEventListener("online", nochEinmal);
  }, [ohneNetz, nochEinmal]);

  // Am Gerät: gleich, noch im Tippen – sonst ließe das Handy keinen neuen Tab auf.
  if (k2.pdf) {
    const pdf = k2.pdf;
    return (
      <button type="button" className="knopf-rand knopf-block" onClick={() => window.open(adresse(new Blob([pdf], { type: "application/pdf" })), "_blank")}>
        <Ikon name="datei" /> PDF ansehen
      </button>
    );
  }
  if (stand?.art === "da") {
    return (
      <a href={stand.url} target="_blank" rel="noreferrer" className="knopf-rand knopf-block">
        <Ikon name="datei" /> PDF ansehen
      </a>
    );
  }
  if (stand?.art === "holt") {
    return (
      <button type="button" className="knopf-rand knopf-block laedt" disabled>
        <span className="kreisel" aria-hidden="true" /> PDF wird aus dem Büro geholt …
      </button>
    );
  }
  return (
    <>
      <button type="button" className="knopf-rand knopf-block" onClick={nochEinmal}>
        <Ikon name="datei" /> PDF ansehen – noch einmal holen
      </button>
      {stand?.art === "ohne-netz" && (
        <p className="klein-text pdf-hinweis">Ohne Empfang – das PDF liegt sicher im Büro und kommt, sobald wieder Netz da ist.</p>
      )}
      {stand?.art === "fehlt" && <p className="klein-text pdf-hinweis">Das PDF ist gerade weder am Gerät noch im Büro zu bekommen.</p>}
    </>
  );
}

/**
 * Der zweite Platz (seit E3c, S5): die Liste – der Excel-Report von K2 – für
 * den Lieferanten. Ablegen und ersetzen, nicht entfernen (Vertrag im Kern).
 * Ansehen geht im CRM; hier stehen Name und Größe.
 */
function ListePlatz({ k2, inArbeit, gewaehlt }: { k2: K2 | null; inArbeit: boolean; gewaehlt: (datei: File) => void }) {
  const excel = k2?.excel ?? null;
  return excel ? (
    <div className="k2-karte">
      <div className="k2-datei">
        <span className="avatar avatar-xls">XLS</span>
        <span className="k2-datei-wort">
          <b>{excel.dateiname ?? "K2-Liste.xlsx"}</b>
          <span>
            {excel.groesse > 0 ? `${groesseZeigen(excel.groesse)} · ` : ""}
            {excel.daten ? "abgelegt bei der Anlage" : "liegt im Büro"}
          </span>
        </span>
        <Ikon name="haken" />
      </div>
      <label className={`knopf-rand knopf-block${inArbeit ? " laedt" : ""}`}>
        {inArbeit ? (
          <>
            <span className="kreisel" aria-hidden="true" /> Wird geprüft …
          </>
        ) : (
          <>
            <Ikon name="hochladen" /> Andere Liste ablegen
          </>
        )}
        <PdfFeld art="excel" gewaehlt={gewaehlt} />
      </label>
    </div>
  ) : (
    <label className="foto-neu breit">
      {inArbeit ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="liste" groesse="g" />}
      <span className="foto-neu-wort">
        <b>{inArbeit ? "Wird geprüft …" : "Liste ablegen"}</b>
        <span>Der Excel-Report aus K2 (.xlsx)</span>
      </span>
      <PdfFeld art="excel" gewaehlt={gewaehlt} />
    </label>
  );
}

function K2Inhalt({ ich, ganz }: { ich: IchAntwort; ganz: AufnahmeGanz }) {
  const { aufnahme, k2 } = ganz;
  const post = usePostausgang();
  const [fehler, setFehler] = useState<string | null>(null);
  const [inArbeit, setInArbeit] = useState<"pdf" | "excel" | null>(null);
  // „Dachplanung starten“ (seit S5): die Adresse von K2 am Gerät – beim Öffnen auffrischen (höchstens einmal je Minute).
  const firmaId = ich.firma.id;
  const k2Ziel = k2AdresseZumOeffnen(useLive(() => k2AdresseLesen(firmaId), `k2-adresse-${firmaId}`));
  useEffect(() => {
    standardlisteAnstossen(firmaId);
  }, [firmaId]);
  const [rueckfrage, setRueckfrage] = useState(false);
  const rueckfrageKasten = useRef<HTMLDivElement>(null);
  // Die Rückfrage ganz zeigen – sonst lägen ihre Knöpfe unter dem festen Fuß.
  useEffect(() => {
    if (rueckfrage) rueckfrageKasten.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [rueckfrage]);

  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;
  const pdf = k2?.pruefsumme ? k2 : null;
  // Danach: abschließen, wenn alle Stationen fertig sind – sonst (oder schon gesendet) zur Übersicht.
  const zumAbschluss = aenderbar(aufnahme) && stationenFertig(ganz.stationen) === STATIONEN_ZAHL;
  const danach = zumAbschluss ? `${zurUebersicht}/senden` : zurUebersicht;

  /** Eine gewählte Datei prüfen und ablegen – das PDF (der Plan) oder seit S5 die Liste (der Excel-Report). */
  async function ablegen(
    art: "pdf" | "excel",
    datei: File,
    vorbereiten: (d: File) => Promise<PdfPruefung>,
    ablegenAm: (a: typeof aufnahme, bereit: PdfBereit) => Promise<void>,
  ) {
    const name = art === "pdf" ? "Das PDF" : "Die Liste";
    setFehler(null);
    setInArbeit(art);
    try {
      const geprueft = await vorbereiten(datei);
      if (!geprueft.ok) {
        setFehler(geprueft.meldung);
        return;
      }
      await ablegenAm(aufnahme, geprueft.pdf);
      toastZeigen(art === "pdf" ? "PDF abgelegt" : "Liste abgelegt");
    } catch (f) {
      fehlerMelden("doku", f);
      setFehler(keinPlatz(f) ? "Kein Platz mehr am Gerät – bitte Speicher freigeben." : `${name} ließ sich nicht ablegen – bitte noch einmal.`);
    } finally {
      setInArbeit(null);
    }
  }
  const gewaehlt = (datei: File) => void ablegen("pdf", datei, pdfVorbereiten, k2PdfAblegen);
  const listeGewaehlt = (datei: File) => void ablegen("excel", datei, excelVorbereiten, k2ExcelAblegen);

  async function ueberspringen(weiter: "weiter" | "bleiben") {
    setRueckfrage(false);
    setFehler(null);
    try {
      await k2Ueberspringen(aufnahme);
      toastZeigen(weiter === "weiter" ? "Dachplanung übersprungen" : "PDF entfernt");
      if (weiter === "weiter") window.location.hash = danach;
    } catch (f) {
      fehlerMelden("doku", f);
      toastZeigen("Konnte nicht speichern – bitte noch einmal.", "fehler");
    }
  }

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: zurUebersicht, name: "Übersicht" }}
        titel="Dachplanung"
        rechts={<SendeStand wartend={post.wartend} />}
        gross="Dachplanung mit K2"
        unter="Plan und Liste aus K2 – dürfen auch später kommen"
      />
      <main className="app-inhalt">
        {/* Selbsterklärend (G19): oben ein Satz, wie es geht. */}
        <p className="k2-einleitung">
          {k2Ziel ? "In K2 das Dach planen, dann hier ablegen: " : "Das Dach in K2 planen, dann hier ablegen: "}
          <b>Der Plan geht an die Dachpartie, die Liste an den Lieferanten.</b>
        </p>

        {k2Ziel && (
          <a href={k2Ziel} target="_blank" rel="noreferrer" className="knopf-haupt knopf-block">
            <Ikon name="dach" /> Dachplanung starten
          </a>
        )}

        {fehler && (
          <Tipp art="rot" ikon="achtung">
            {fehler}
          </Tipp>
        )}

        <div className="abschnitt-zeile">
          <div className="abschnitt-titel">Der Plan (PDF)</div>
          <span className="klein-text">für die Dachpartie</span>
        </div>
        {pdf ? (
          <div className="k2-karte">
            <div className="k2-datei">
              <span className="avatar avatar-pdf">PDF</span>
              <span className="k2-datei-wort">
                <b>{pdf.dateiname ?? "Dachplanung.pdf"}</b>
                <span>
                  {pdf.groesse > 0 ? `${groesseZeigen(pdf.groesse)} · ` : ""}
                  {pdf.pdf ? "abgelegt bei der Anlage" : "liegt im Büro"}
                </span>
              </span>
              <Ikon name="haken" />
            </div>
            <PdfAnsehen key={pdf.pruefsumme} k2={pdf} />
            <label className={`knopf-rand knopf-block${inArbeit === "pdf" ? " laedt" : ""}`}>
              {inArbeit === "pdf" ? (
                <>
                  <span className="kreisel" aria-hidden="true" /> Wird geprüft …
                </>
              ) : (
                <>
                  <Ikon name="hochladen" /> Anderes PDF ablegen
                </>
              )}
              <PdfFeld gewaehlt={gewaehlt} />
            </label>
            {/* Bei den anderen PDF-Knöpfen, nicht ganz unten (Gernots Augentest, 20.10.2026: erst nach dem Scrollen zu sehen). */}
            {rueckfrage ? (
              <div ref={rueckfrageKasten} className="rueckfrage" role="alertdialog" aria-labelledby="k2-rueckfrage-titel">
                <b id="k2-rueckfrage-titel">Das PDF entfernen?</b>
                <span>Die Dachplanung gilt dann als übersprungen. Ein PDF kann jederzeit nachkommen.</span>
                <div className="knopf-reihe">
                  <button type="button" className="knopf-rand" onClick={() => setRueckfrage(false)}>
                    Behalten
                  </button>
                  <button type="button" className="knopf-loeschen" onClick={() => void ueberspringen("bleiben")}>
                    <Ikon name="muell" /> Entfernen
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="knopf-leise-rot" onClick={() => setRueckfrage(true)}>
                <Ikon name="muell" /> PDF entfernen
              </button>
            )}
          </div>
        ) : (
          <>
            <label className="foto-neu breit">
              {inArbeit === "pdf" ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="datei" groesse="g" />}
              <span className="foto-neu-wort">
                <b>{inArbeit === "pdf" ? "Wird geprüft …" : "PDF ablegen"}</b>
                <span>Der Plan der Dachplanung aus K2</span>
              </span>
              <PdfFeld gewaehlt={gewaehlt} />
            </label>
            {k2?.uebersprungen && (
              <Tipp art="grau" ikon="haken">
                <b>Übersprungen.</b> Das PDF kann jederzeit nachkommen – auch nach dem Senden.
              </Tipp>
            )}
          </>
        )}

        <div className="abschnitt-zeile">
          <div className="abschnitt-titel">Die Liste (Excel)</div>
          <span className="klein-text">für den Lieferanten</span>
        </div>
        <ListePlatz k2={k2} inArbeit={inArbeit === "excel"} gewaehlt={listeGewaehlt} />

        <Spaeter titel="Plan und Liste kommen von selbst zurück" wann="Stufe 2">
          Nach der Planung in K2 liegen PDF und Excel von selbst hier – ohne Ablegen.
        </Spaeter>
        <Spaeter titel="Stückliste automatisch auslesen" wann="Stufe 2">
          Module und Schienen aus dem K2-PDF landen von selbst in der Materialliste.
        </Spaeter>
        <Spaeter titel="Kunde bestätigt mit Unterschrift" wann="Stufe 2">
          Die Kundin sieht ihre Anlage und unterschreibt gleich am Surface.
        </Spaeter>
      </main>
      <AppFuss>
        {k2Erledigt(k2) ? (
          <a href={danach} className="knopf-haupt knopf-block">
            {zumAbschluss ? "Weiter: Abschließen" : "Weiter zur Übersicht"} <Ikon name="rechts" />
          </a>
        ) : (
          <button type="button" className="knopf-rand knopf-block" onClick={() => void ueberspringen("weiter")}>
            Überspringen
          </button>
        )}
      </AppFuss>
    </div>
  );
}

export function K2Seite({ ich, aufnahmeId }: { ich: IchAntwort; aufnahmeId: string }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return <div className="app-seite" />;
  if (ganz === null) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Dachplanung" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Besichtigung ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }
  return <K2Inhalt ich={ich} ganz={ganz} />;
}
